import {
  CardEffect,
  Cosmetic,
  FOIL_FRAME_MS,
  FOIL_GLOW,
  Foil,
  cardEffects,
  foilCalm,
} from "@repo/shared/types";
import { useEffect, useMemo, useRef } from "react";

// Cell k is k/(frames - 1) of the way across the strip.
const positionAt = (k: number, frames: number) =>
  `${(100 * (k % frames)) / (frames - 1)}%`;

const decoded = (src: string) => {
  const image = new Image();
  image.src = src;
  return image.decode();
};

// One pass of a layer's strip, cross-faded cell to cell since snapping at
// ~11fps judders. Nothing fills, so it all drops back to rest at the end.
const pass = (layer: Element, { strip }: CardEffect) => {
  if (!strip) return;
  const [still, lead, trail] = layer.children;
  const { animated, frames } = strip;
  const duration = frames * FOIL_FRAME_MS;

  still.animate({ opacity: [0, 0] }, duration);
  [lead, trail].forEach((copy, next) => {
    copy.animate(
      Array.from({ length: frames + 1 }, (_, i) => ({
        // Only drawn during the pass, so the strip loads on the first hover.
        backgroundImage: `url(${animated})`,
        backgroundPositionX: positionAt(i + next, frames),
        easing: "steps(1)",
      })),
      duration,
    );
    copy.animate(
      { opacity: next ? [0, 1] : [1, 0] },
      { duration: FOIL_FRAME_MS, iterations: frames },
    );
  });
};

// A card's cosmetics, then its foil, over its positioned parent. A hover plays
// each strip once, even if the pointer leaves. See ADR 0026 and be ADR 0035.
export const CardEffects = ({
  foil,
  cosmetics,
  cardUrl,
  playOnShow,
}: {
  foil?: Foil;
  cosmetics?: Cosmetic[];
  /** The version art, redrawn to cast the foil's glow. */
  cardUrl: string;
  /** Also plays once on arrival, for the main card on a screen. */
  playOnShow?: boolean;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const layers = useMemo(() => cardEffects(foil, cosmetics), [foil, cosmetics]);

  useEffect(() => {
    const root = ref.current;
    const card = root?.parentElement;
    const strips = layers.flatMap(({ strip }) =>
      strip ? [strip.animated] : [],
    );
    if (!root || !card || !strips.length) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let live = true;
    // The still images stay up until every strip has loaded.
    const loaded = () => Promise.all(strips.map(decoded));
    const run = () => {
      // A trigger mid-pass is ignored; the next one after it replays.
      if (!live || root.getAnimations({ subtree: true }).length) return;
      layers.forEach((layer, i) => pass(root.children[i], layer));
    };
    const play = () => loaded().then(run, () => {});
    card.addEventListener("pointerenter", play);
    // Not while the page is still loading: a pass started under it stutters.
    if (playOnShow) Promise.all([loaded(), foilCalm()]).then(run, () => {});
    return () => {
      live = false;
      card.removeEventListener("pointerenter", play);
    };
  }, [layers, playOnShow]);

  if (!layers.length) return null;

  return (
    <>
      {foil && foil.frames >= 1 && <Glow cardUrl={cardUrl} />}
      <div
        ref={ref}
        aria-hidden
        className="absolute inset-0 pointer-events-none"
      >
        {layers.map(({ still, cells, strip }) => (
          // `isolate` keeps the trail's `plus-lighter` blending with the lead
          // copy rather than with the card underneath.
          <div key={still} className="absolute inset-0 isolate">
            <Cells cells={cells} style={{ backgroundImage: `url(${still})` }} />
            {strip && (
              <>
                <Cells cells={strip.frames} style={{ opacity: 0 }} />
                <Cells
                  cells={strip.frames}
                  style={{ opacity: 0, mixBlendMode: "plus-lighter" }}
                />
              </>
            )}
          </div>
        ))}
      </div>
    </>
  );
};

// `cells` card-wide cells side by side, showing the first. Styled inline, as
// our `!important` utilities would beat a pass's animations.
const Cells = ({
  cells,
  style,
}: {
  cells: number;
  style: React.CSSProperties;
}) => (
  <div
    className="absolute inset-0 bg-no-repeat"
    style={{ backgroundSize: `${cells * 100}% 100%`, ...style }}
  />
);

/**
 * The halo. `drop-shadow` derives its shape from alpha, so redrawing the card
 * art in its own layer traces the card's notched silhouette — a `box-shadow`,
 * or the same filter on the card container, would square it off or drag every
 * child's shadow along with it. The redraw lands exactly on the art already
 * there, so only the spill outside the card is new.
 */
const Glow = ({ cardUrl }: { cardUrl: string }) => (
  <div
    aria-hidden
    className="absolute inset-0 bg-no-repeat pointer-events-none"
    style={{
      backgroundImage: `url(${cardUrl})`,
      backgroundSize: "100% 100%",
      // Chained, so each lobe also blurs the one before it — the same way the
      // two lobes overlap in the sprite we measured them from.
      filter: FOIL_GLOW.map(
        ({ blur, color }) => `drop-shadow(0 0 ${blur}px ${color})`,
      ).join(" "),
    }}
  />
);
